import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useDebounce } from 'utils/hooks/useDebounce';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import {
  AddLabel,
  AddWrapper,
  Container,
  FirstRow,
  InputWrapper,
  LeftSide,
  RightSide,
  Heading,
  NoContentLabelWrapper,
  NoContentLabel,
  CompaniesWrapper,
  Wrapper,
} from './BuyersPage.styled';
import Input from 'components/Input/Input';
import { Plus, MagnifyingGlass as Search } from '@phosphor-icons/react';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import ViewTypePicker, {
  ViewType,
} from 'components/ViewTypePicker/ViewTypePicker';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import {
  useDeleteBuyer,
  useGetCompanyBuyers,
  useSelectedViewType,
} from './hooks';
import { AddBuyerModal } from 'components/Modals/AddBuyerModal/AddBuyerModal';
import { BUYERS_PER_PAGE, tableConfig } from './constants';
import { RoutesConfig } from 'navigation/routes';
import { useNavigate } from 'react-router-dom';
import { formatBuyersTableData } from 'components/Table/tableDataFormatter';
import { ICompany, ICompanyBuyerDTO } from 'types/Company.types';
import Table from 'components/Table/Table';
import CompanyCard from 'components/CompanyCard/CompanyCard';
import Pagination from 'components/Pagination/Pagination';
import CompanyCardSkeleton from 'components/CompanyCard/CompanyCardSkeleton/CompanyCardSkeleton';

const BuyersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchBy, setSearchBy] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const debouncedSearchBy = useDebounce(searchBy);
  const { selectedViewType, setSelectedViewType } = useSelectedViewType();
  const [isAddBuyerModalOpen, setIsAddBuyerModalOpen] =
    useState<boolean>(false);
  const [isDeleteBuyerModalOpen, setIsDeleteBuyerModalOpen] =
    useState<boolean>(false);
  const [buyerIdForDeletion, setBuyerIdForDeletion] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  usePageTitle('Buyers');

  const { isMidTablet } = useBreakpointFlag();

  useEffect(() => {
    if (isMidTablet && selectedViewType === ViewType.LIST) {
      setSelectedViewType(ViewType.GRID);
    }
  }, [isMidTablet]);

  const handleOpenBuyer = (buyerId: number) => {
    navigate(
      RoutesConfig.BuyersSingle.fullPath.replace(':id', buyerId.toString())
    );
  };

  const { mutate: deleteBuyer, isLoading: isLoadingDeleteBuyer } =
    useDeleteBuyer();

  const { data: buyersData, isLoading: isLoadingBuyers } = useGetCompanyBuyers(
    page,
    BUYERS_PER_PAGE,
    debouncedSearchBy
  );

  let tableData: ICompanyBuyerDTO[] = [];
  if (buyersData?.companies) {
    tableData = formatBuyersTableData(
      buyersData?.companies
        .map((company: ICompany[]) => {
          return company;
        })
        .flat()
    );
  }

  return (
    <Container>
      <FirstRow>
        <LeftSide>
          <Heading>{t('Buyers')}</Heading>
        </LeftSide>
        <RightSide>
          <InputWrapper>
            <Input
              placeholder={t('Search')}
              width={'100%'}
              onChange={(e: any) => setSearchBy(e.target.value)}
              icon={Search}
              wrapperStyles={{ width: '100%' }}
            />
          </InputWrapper>
          {!isMidTablet && (
            <ViewTypePicker
              selectedViewType={selectedViewType}
              setSelectedViewType={setSelectedViewType}
            />
          )}
          {/* <AddWrapper onClick={() => setIsAddBuyerModalOpen(true)}>
            <Icon svg={Plus} color={COLORS.GREEN} size={20} />
            <AddLabel>{t('Add Buyer')}</AddLabel>
          </AddWrapper> */}
        </RightSide>
      </FirstRow>
      {!buyersData?.companies.length && !isLoadingBuyers ? (
        <NoContentLabelWrapper>
          <NoContentLabel>{t('No buyers available')}</NoContentLabel>
        </NoContentLabelWrapper>
      ) : (
        <>
          {selectedViewType === ViewType.GRID ? (
            <Wrapper>
              <CompaniesWrapper>
                {buyersData?.companies.map(
                  (company: ICompany, index: number) => (
                    <CompanyCard
                      key={`company-card-${index}`}
                      company={company}
                      onClick={(company: ICompany) => {
                        handleOpenBuyer(company.id);
                      }}
                      // onDelete={(buyerId: number) => {
                      //   setBuyerIdForDeletion(buyerId);
                      //   setIsDeleteBuyerModalOpen(true);
                      // }}
                    />
                  )
                )}
                {isLoadingBuyers
                  ? Array.from({ length: BUYERS_PER_PAGE }, (_, index) => (
                      <CompanyCardSkeleton
                        key={index}
                        index={index}
                        opacityCoefficient={0.1}
                      />
                    ))
                  : null}
              </CompaniesWrapper>
              {buyersData?.pagination?.total > BUYERS_PER_PAGE && (
                <Pagination
                  page={page}
                  perPage={BUYERS_PER_PAGE}
                  total={buyersData?.pagination?.total}
                  setPage={setPage}
                />
              )}
            </Wrapper>
          ) : (
            <Table
              page={page}
              perPage={BUYERS_PER_PAGE}
              total={buyersData?.pagination?.total}
              isLoading={isLoadingBuyers}
              tableData={tableData}
              tableConfig={tableConfig}
              hasExtraAction={true}
              extraActionLabel={t('Open')}
              loadPage={(newPage: number) => setPage(newPage)}
              onViewButtonClick={(rowData: any) => {
                handleOpenBuyer(rowData.navigationID);
              }}
              // onDelete={(rowData: any) => {
              //   setBuyerIdForDeletion(rowData.navigationID);
              //   setIsDeleteBuyerModalOpen(true);
              // }}
            />
          )}
        </>
      )}

      <AddBuyerModal
        isOpen={isAddBuyerModalOpen}
        setIsOpen={setIsAddBuyerModalOpen}
      />

      <DangerModal
        isOpen={isDeleteBuyerModalOpen}
        setIsOpen={setIsDeleteBuyerModalOpen}
        title={t('Confirm deletion')}
        submit={{
          onClick: () => {
            deleteBuyer(buyerIdForDeletion!);
            setIsDeleteBuyerModalOpen(false);
            setBuyerIdForDeletion(null);
          },
          text: t('Delete'),
          disabled: isLoadingDeleteBuyer,
        }}
        cancel={{
          onClick: () => {
            setIsDeleteBuyerModalOpen(false);
            setBuyerIdForDeletion(null);
          },
        }}
        description={t('Are you sure you want to delete this Buyer?')}
      />
    </Container>
  );
};

export default BuyersPage;
